/**
 * What media-api's guideline endpoints must send for the standards surface to render them.
 *
 * `looseObject` throughout, and closed unions read through {@link wireUnion}: see
 * `entities/media-campaign/api/schemas.ts` for why both hold across this surface.
 */
import { z } from 'zod';
import { wireUnion } from '@/lib/api/parse-response';
import type {
  ListMediaGuidelinesResponse,
  MediaGuideline,
  MediaGuidelineVersion,
  QaAuditEntry,
} from '../model/types';
import type { PlatformPlanRules } from './queries';

const guidelineVersionSchema: z.ZodType<MediaGuidelineVersion> = z.looseObject({
  id: z.string(),
  versionNumber: z.number(),
  parentVersionId: z.string().nullable(),
  status: wireUnion<MediaGuidelineVersion['status']>(),
  level: wireUnion<MediaGuidelineVersion['level']>(),
  tier: wireUnion<MediaGuidelineVersion['tier']>(),
  rule: z.looseObject({}),
  why: z.string().nullable(),
  howToFix: z.string().nullable(),
  referenceUrl: z.string().nullable(),
  ownerName: z.string().nullable(),
  revision: z.number(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const mediaGuidelineSchema: z.ZodType<MediaGuideline> = z.looseObject({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: wireUnion<MediaGuideline['kind']>(),
  platform: z.string().nullable(),
  campaignCategory: z.string().nullable(),
  status: wireUnion<MediaGuideline['status']>(),
  currentVersionId: z.string().nullable(),
  lastVerifiedAt: z.string().nullable(),
  supersededById: z.string().nullable(),
  libraryKey: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Sent on a detail read only, which is what `effectiveVersion` and its siblings default for.
  versions: z.array(guidelineVersionSchema).optional(),
});

export const guidelineListSchema: z.ZodType<ListMediaGuidelinesResponse> = z.looseObject({
  items: z.array(mediaGuidelineSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export const guidelineHistorySchema: z.ZodType<QaAuditEntry[]> = z.array(
  z.looseObject({
    id: z.string(),
    actorId: z.string(),
    action: z.string(),
    reason: z.string().nullable(),
    guidelineVersionId: z.string().nullable(),
    jobId: z.string().nullable(),
    nodeId: z.string().nullable(),
    metadata: z.looseObject({}),
    createdAt: z.string(),
  }),
);

export const guidelineCategoriesSchema = z.array(z.string());

export const planRulesSchema: z.ZodType<{ platforms: PlatformPlanRules[] }> = z.looseObject({
  platforms: z.array(
    z.looseObject({
      platform: z.string(),
      label: z.string(),
      rules: z.array(
        z.looseObject({
          id: z.string(),
          level: z.string(),
          severity: wireUnion<PlatformPlanRules['rules'][number]['severity']>(),
          describe: z.string(),
          platformSpecific: z.boolean(),
        }),
      ),
    }),
  ),
});
